import hljs from "highlight.js"
import "highlight.js/styles/atom-one-dark.css"
import { useCurrentId } from "hooks/useCurrentId.ts"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { getTintVTOService } from "services/tintvto"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ArrowLeft, Check, Copy, QrCode } from "lucide-react"
import { useAppSelector } from "@/hooks/storeHooks"
import { selectUserInfo } from "@/store/user/userSlice"
import { IntegrationQrGenerator } from "./IntegrationQrGenerator"

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={handleCopy}
      className="gap-1.5"
      data-testid="copy-code-button"
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  )
}

export const IntegrationInfo = () => {
  const { t } = useTranslation()
  const routeMerchantId = useCurrentId()
  const userInfo = useAppSelector(selectUserInfo)
  const isAdmin = userInfo.groups.some((group) => group.trim().toLowerCase() === "admin")
  const merchantId = isAdmin && routeMerchantId ? routeMerchantId : userInfo.merchantId
  const showBackToMerchants = isAdmin && Boolean(routeMerchantId)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [merchantName, setMerchantName] = useState("")

  const codeBlocks = [
    {
      title: t("integrationInfo.addScriptImport"),
      code: `<script type="module" src="https://tintvto.com/widget.js"></script>`,
    },
    {
      title: t("integrationInfo.addToIndexHtml"),
      code: `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width" />
    <title>Your Website Title</title>

    <!-- import of TINT-vto widget -->
    <script type="module" src="tint-vto/widget/index.js"></script>
  </head>
  <body></body>
</html>`,
    },
    {
      title: t("integrationInfo.runWidget"),
      code: `<tint-vto merchant-id="${merchantId}" />`,
    },
    {
      title: t("integrationInfo.openMethodDescription"),
      code: `<script type="module">
  !(async () => {
    const widget = document.querySelector("tint-vto")
    await widget.open()
  })()
</script>`,
    },
  ]

  useEffect(() => {
    if (!merchantId) return
    ;(async () => {
      const service = getTintVTOService()
      const merchantData = await service.merchantById(merchantId)
      const merchantName = merchantData.data?.merchantById?.name?.trim()
      if (!merchantName) return

      setMerchantName(t("integrationInfo.merchantName", { name: merchantName }))
    })()
  }, [merchantId, t])

  useEffect(() => {
    if (contentRef.current)
      contentRef.current
        .querySelectorAll<HTMLElement>("pre code")
        .forEach((block) => hljs.highlightElement(block))
  }, [codeBlocks])

  return (
    <div className="p-6" ref={contentRef} data-testid="integration-info-page">
      <div className="flex max-w-5xl flex-col gap-3">
        <div>
          {showBackToMerchants ? (
            <Link
              to="/merchants"
              data-testid="integration-info-back"
              className="mb-2 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              {t("integrationInfo.backToMerchants")}
            </Link>
          ) : null}
          <h1
            className="text-2xl font-semibold tracking-tight"
            data-testid="integration-info-title"
          >
            {merchantName
              ? `${t("integrationInfo.title")} ${merchantName}`
              : t("integrationInfo.title")}
          </h1>
        </div>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <h2 className="flex items-center gap-2 text-base font-semibold">
              <QrCode data-icon="inline-start" />
              {t("integrationInfo.qrCode.title")}
            </h2>
          </div>
          {merchantId ? <IntegrationQrGenerator merchantId={merchantId} /> : null}
        </div>

        <div className="flex flex-col gap-3">
          <h2 className="text-base font-semibold">{t("integrationInfo.cdnIntegrationTitle")}</h2>
          {codeBlocks.map((block, i) => (
            <Card key={i} data-testid="integration-code-block">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-sm font-medium">{block.title}</CardTitle>
                <CopyButton text={block.code} />
              </CardHeader>
              <CardContent>
                <pre className="overflow-x-auto rounded-md">
                  <code className="language-xml text-sm">{block.code}</code>
                </pre>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
